import { ResourceType, type ResourceOutput } from '@cotera/api';
import { Lazy } from './lazy';
import { fromIsoString, toIsoString } from './iso-date';

export type FolderScope = 'user' | 'org' | 'external';

export type FolderResourceOutput = Extract<ResourceOutput, { type: 'folder' }>;

/**
 * Membership row (entity in a folder). Aligns with server `FolderItemRef` / folder_items.
 */
export type FolderItemRef = {
  readonly entityType: ResourceType;
  readonly entityId: string;
  readonly position: number;
};

export type FolderData = {
  readonly id: string;
  readonly ownerOrgId: string;
  readonly ownerId: string;
  readonly parentId: string | null;
  readonly name: string;
  readonly position: number;
  readonly scope: FolderScope;
  readonly scopeData?: Record<string, unknown>;
  readonly childrenCount?: number;
  /** Chats filed directly in the folder; the Library lists them as one row. */
  readonly chatCount?: number;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedFolderItemRef = {
  readonly entityType: ResourceType;
  readonly entityId: string;
  readonly position: number;
};

export type SerializedFolder = {
  readonly id: string;
  readonly ownerOrgId: string;
  readonly ownerId: string;
  readonly parentId: string | null;
  readonly name: string;
  readonly position: number;
  readonly scope: FolderScope;
  readonly scopeData?: Record<string, unknown>;
  readonly childrenCount?: number;
  /** Chats filed directly in the folder; the Library lists them as one row. */
  readonly chatCount?: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly items?: readonly SerializedFolderItemRef[];
};

export type FolderHydration = {
  readonly loadParent: () => Promise<Folder | null>;
  readonly loadItems: () => Promise<readonly FolderItemRef[]>;
  readonly preloadedParent?: Folder | null;
  readonly preloadedItems?: readonly FolderItemRef[];
};

function toFolderData(serialized: SerializedFolder): FolderData {
  return {
    id: serialized.id,
    ownerOrgId: serialized.ownerOrgId,
    ownerId: serialized.ownerId,
    parentId: serialized.parentId,
    name: serialized.name,
    position: serialized.position,
    scope: serialized.scope,
    scopeData: serialized.scopeData,
    childrenCount: serialized.childrenCount,
    chatCount: serialized.chatCount,
    createdAt: fromIsoString(serialized.createdAt),
    updatedAt: fromIsoString(serialized.updatedAt),
  };
}

function mapSerializedItems(
  items: readonly SerializedFolderItemRef[]
): readonly FolderItemRef[] {
  return items.map((i) => ({
    entityType: i.entityType,
    entityId: i.entityId,
    position: i.position,
  }));
}

/**
 * Workspace folder (tree node) with lazy parent and member listing.
 */
export class Folder {
  private readonly _data: FolderData;
  readonly parent: Lazy<Folder | null>;
  readonly items: Lazy<readonly FolderItemRef[]>;

  private constructor(data: FolderData, hydration: FolderHydration) {
    this._data = data;
    this.parent = new Lazy(hydration.loadParent, hydration.preloadedParent);
    this.items = new Lazy(hydration.loadItems, hydration.preloadedItems);
  }

  get id(): string {
    return this._data.id;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get parentId(): string | null {
    return this._data.parentId;
  }

  get name(): string {
    return this._data.name;
  }

  get position(): number {
    return this._data.position;
  }

  get scope(): FolderScope {
    return this._data.scope;
  }

  get scopeData(): Record<string, unknown> | undefined {
    return this._data.scopeData;
  }

  get childrenCount(): number | undefined {
    return this._data.childrenCount;
  }

  get chatCount(): number | undefined {
    return this._data.chatCount;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get folderData(): FolderData {
    return { ...this._data };
  }

  /** Chats in this folder (by membership), ordered by position. */
  async chats(): Promise<readonly FolderItemRef[]> {
    return this._itemsByType(ResourceType.Chat);
  }

  /** Agents in this folder, ordered by position. */
  async agents(): Promise<readonly FolderItemRef[]> {
    return this._itemsByType(ResourceType.Agent);
  }

  /** Documents in this folder, ordered by position. */
  async documents(): Promise<readonly FolderItemRef[]> {
    return this._itemsByType(ResourceType.Document);
  }

  /** Child folder entities (folder-in-folder), ordered by position. */
  async childFolderRefs(): Promise<readonly FolderItemRef[]> {
    return this._itemsByType(ResourceType.Folder);
  }

  private async _itemsByType(
    entityType: ResourceType
  ): Promise<readonly FolderItemRef[]> {
    const all = await this.items.get();
    return [...all]
      .filter((i) => i.entityType === entityType)
      .sort((a, b) => a.position - b.position);
  }

  serialize(): SerializedFolder {
    const base: SerializedFolder = {
      id: this._data.id,
      ownerOrgId: this._data.ownerOrgId,
      ownerId: this._data.ownerId,
      parentId: this._data.parentId,
      name: this._data.name,
      position: this._data.position,
      scope: this._data.scope,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
    const peekItems = this.items.peek();
    const withOptional: SerializedFolder = {
      ...base,
      ...(this._data.scopeData !== undefined
        ? { scopeData: this._data.scopeData }
        : {}),
      ...(this._data.childrenCount !== undefined
        ? { childrenCount: this._data.childrenCount }
        : {}),
      ...(this._data.chatCount !== undefined
        ? { chatCount: this._data.chatCount }
        : {}),
      ...(peekItems !== undefined
        ? {
            items: peekItems.map((i) => ({
              entityType: i.entityType,
              entityId: i.entityId,
              position: i.position,
            })),
          }
        : {}),
    };
    return withOptional;
  }

  static fromSerialized(
    serialized: SerializedFolder,
    hydration: FolderHydration
  ): Folder {
    const data = toFolderData(serialized);
    const inlineItems =
      serialized.items === undefined
        ? undefined
        : mapSerializedItems(serialized.items);
    return new Folder(data, {
      ...hydration,
      preloadedItems: hydration.preloadedItems ?? inlineItems,
    });
  }

  static fromResourceOutput(
    output: FolderResourceOutput,
    ownerOrgId: string,
    hydration: FolderHydration
  ): Folder {
    const f = output.folder;
    const serialized: SerializedFolder = {
      id: f.id,
      ownerOrgId,
      ownerId: f.ownerId,
      parentId: f.parentId,
      name: f.name,
      position: f.position,
      scope: f.scope,
      createdAt: toIsoString(f.createdAt),
      updatedAt: toIsoString(f.updatedAt),
      ...(f.scopeData !== undefined ? { scopeData: f.scopeData } : {}),
      ...(f.childrenCount !== undefined
        ? { childrenCount: f.childrenCount }
        : {}),
      ...(f.chatCount !== undefined ? { chatCount: f.chatCount } : {}),
    };
    return Folder.fromSerialized(serialized, hydration);
  }
}

/**
 * Resolves to no folder: use for {@link FolderHydration.loadParent} at tree root, or
 * {@link AgentHydration.loadFolder} / {@link DocumentHydration.loadFolder} (and
 * {@link AgentHydration.workspaceDomainLoader} / {@link DocumentHydration.workspaceDomainLoader})
 * when there is no folder or workspace graph.
 */
export async function resolveNoFolder(): Promise<Folder | null> {
  return null;
}

/** Use for {@link FolderHydration.loadItems} when membership is only supplied via `preloadedItems`. */
export async function resolveEmptyFolderItems(): Promise<
  readonly FolderItemRef[]
> {
  return [];
}
